<style lang="scss">
@import '../../resources/css/main.css';

.anchor-content {
  background: #f2f7f8 !important;
  height: calc(100% - 54px) !important;
  padding: 1rem !important;
}

.anchor-footer {
  color: #54667a !important;
  background: #ffffff !important;
  border-top: 1px solid rgba(120, 130, 140, 0.13) !important;
  padding: 17px 15px !important;
  font-size: .8rem !important;
}

.anchor-drawer {
  background-color: #272c33 !important;
  color: #798699 !important;
}

.anchor-drawer-list {
  font-size: 15px !important;
  font-weight: 300;
  padding-top: 30px;
}

.anchor-drawer-list .v-list-item__title {
  font-size: 15px !important;
}

.link-color {
  color: rgba(0, 0, 0, 0.87) !important;
}

.reload-container {
  text-align: right;
}

.reload-container * {
  color: rgba(0, 0, 0, 0.54) !important;
}
</style>

<template>
  <v-app>
    <v-hover
      v-slot:default="{ hover }"
      :disabled="!mini && !breakpoint"
    >
      <v-navigation-drawer
        v-if="sidebar"
        app
        dark
        class="anchor-drawer"
        :clipped="breakpoint"
        :mini-variant="mini && !hover && !focused"
        :width="sidebarWidth"
        :value="drawer"
        @input="handleDrawerInput"
      >
        <v-list class="anchor-drawer-list">
          <slot name="menu" :expanded="expanded || hover"></slot>
          <slot name="quick-links-menu" :expanded="expanded || hover">
            <anchor-sidebar-divider />
            <sidebar-quick-links />
          </slot>
        </v-list>
      </v-navigation-drawer>
    </v-hover>

    <v-app-bar
      id="anchor-app-bar"
      app
      light
      height="70"
      :color="nonProd ? oxfordBlueColorHex : 'white'"
      :clipped-left="breakpoint"
    >
      <v-app-bar-nav-icon
        v-if="sidebar"
        id="sidebar-toggle-btn"
        :color="nonProd ? 'white' : 'black'"
        :disabled="focused"
        @click.stop="toggleSidebar"
      />
      <v-spacer />
      <v-toolbar-title
        style="width: 300px"
        class="ml-0 pl-4"
      >
        <span class="cursor-pointer" @click="onLogoClicked">
          <v-img
            height="50"
            :contain="true"
            :src="logo"
          ></v-img>
        </span>
      </v-toolbar-title>
      <v-spacer />
      <slot name="right-nav-items"></slot>
      <anchor-apps-menu
        v-if="user"
        :user="user"
        :non-prod="nonProd"
      />
      <slot name="user-menu">
        <div id="userMenuAnchor">
          <anchor-user-menu
            v-if="user"
            :user="user"
            :logout-method="logoutMethod"
            :non-prod="nonProd"
          />
        </div>
      </slot>
    </v-app-bar>

    <v-content>
      <slot name="subheader"></slot>
      <v-container class="anchor-content" fluid>
        <div class="d-flex mt-2 mb-6">
          <div>
            <span class="link-color title font-weight-regular">
              {{ title }}
            </span>
            <v-breadcrumbs large :items="crumbs" class="pa-0">
              <template v-slot:divider>
                <v-icon>mdi-chevron-right</v-icon>
              </template>
            </v-breadcrumbs>
          </div>
          <v-spacer />
          <div class="reload-container">
            <div>
              <small>Last updated: {{ loadedAt|datetime }}</small>
            </div>
            <div>
              <v-btn text x-small @click="reload">
                <v-icon small class="mr-2">mdi-reload</v-icon>
                Reload Page
              </v-btn>
            </div>
          </div>
        </div>
        <slot></slot>
      </v-container>
      <v-footer class="anchor-footer">
        <span class="mr-1">&copy; {{ new Date().getFullYear() }}</span>
        Anchor Loans
      </v-footer>
    </v-content>
  </v-app>
</template>

<script>
/* eslint global-require: "off" */
import AnchorAppsMenu from './AppsMenu';
import AnchorUserMenu from '../user/Menu';
import SidebarQuickLinks from '../utils/sidebar/QuickLinks';

export default {
  components: {
    AnchorAppsMenu,
    AnchorUserMenu,
    SidebarQuickLinks,
  },
  props: {
    user: Object,
    logoutMethod: Function,
    sidebar: {
      type: Boolean,
      default: true,
    },
    sidebarWidth: [Number, String],
  },
  data: () => ({
    oxfordBlueColorHex: '#051F3A',
    loadedAt: new Date(),
    drawer: null,
    mini: true,
    focused: false,
    logos: {
      dark: require('../../resources/imgs/logo-dark.png'),
      light: require('../../resources/imgs/logo-light.png'),
    },
  }),
  computed: {
    breakpoint: vm => vm.$vuetify.breakpoint.lgAndUp,
    expanded: vm => (vm.breakpoint ? !vm.mini : vm.drawer),
    nonProd: () => ['development', 'testing', 'staging'].includes(process.env.NODE_ENV),
    logo: vm => vm.logos[vm.nonProd ? 'dark' : 'light'],
    crumbs() {
      if (!this.$route) { return []; }

      const arr = [];
      this.$route.matched.forEach((route, rIdx) => {
        const breadcrumbs = route.meta.breadcrumbs || [];
        const lastIndex = breadcrumbs.length - 1;
        breadcrumbs.forEach((bcrumb, idx) => {
          let href = null;
          if (bcrumb.to) {
            href = `#${this.$router.resolve(bcrumb.to).route.path}`;
          } else if (idx === lastIndex) {
            href = `#${this.$route.matched[rIdx].path}`;
          }
          arr.push({
            href,
            text: bcrumb.title,
            disabled: bcrumb.disabled,
          });
        });
      });

      return arr;
    },
    title() {
      if (!this.$route) { return '-'; }
      const routes = this.$route.matched;
      if (routes.length <= 0) { return '-'; }
      return routes[routes.length - 1].meta.title || '-';
    },
  },
  methods: {
    focus() {
      this.focused = true;
    },
    blur() {
      this.focused = false;
    },
    reload() {
      this.$router.go();
    },
    onLogoClicked() {
      window.location = this.$servers.USERS;
    },
    toggleSidebar() {
      if (this.breakpoint) {
        this.mini = !this.mini;
      } else {
        this.mini = false;
        this.drawer = !this.drawer;
      }
    },
    handleDrawerInput(value) {
      if (!this.breakpoint) {
        this.drawer = value;
      }
    },
  },
  watch: {
    $route() {
      this.loadedAt = new Date();
    },
  },
};
</script>
